export type ExportFormat = 'csv' | 'xlsx';

const csvMimeType = 'text/csv;charset=utf-8;';
const xlsxMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const textEncoder = new TextEncoder();

export const getFilenameFromContentDisposition = (
    contentDisposition: string | undefined,
    fallback: string,
): string => {
    if (!contentDisposition) return fallback;

    const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (encodedMatch?.[1]) return decodeURIComponent(encodedMatch[1].replace(/"/g, ''));

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1] || fallback;
};

export const withFileExtension = (filename: string, extension: 'csv' | 'xlsx'): string => {
    const basename = filename.replace(/\.(csv|xlsx)$/i, '');
    return `${basename}.${extension}`;
};

export const downloadCsvFile = (content: unknown, filename: string): void => {
    downloadBlob(toText(content), withFileExtension(filename, 'csv'), csvMimeType);
};

export const downloadXlsxFromCsv = (
    content: unknown,
    filename: string,
    sheetName = 'Relatorio',
): void => {
    const rows = parseCsv(toText(content));
    const workbook = createXlsxWorkbook(rows, sheetName);
    downloadBlob(workbook, withFileExtension(filename, 'xlsx'), xlsxMimeType);
};

const toText = (content: unknown): string => {
    if (typeof content === 'string') return content;
    if (content == null) return '';
    return JSON.stringify(content);
};

const downloadBlob = (content: BlobPart, filename: string, mimeType: string): void => {
    const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const parseCsv = (csv: string): string[][] => {
    const delimiter = detectDelimiter(csv);
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let inQuotes = false;

    for (let index = 0; index < csv.length; index += 1) {
        const char = csv[index];
        const nextChar = csv[index + 1];

        if (char === '"' && inQuotes && nextChar === '"') {
            cell += '"';
            index += 1;
            continue;
        }

        if (char === '"') {
            inQuotes = !inQuotes;
            continue;
        }

        if (char === delimiter && !inQuotes) {
            row.push(cell);
            cell = '';
            continue;
        }

        if ((char === '\n' || char === '\r') && !inQuotes) {
            if (char === '\r' && nextChar === '\n') index += 1;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
            continue;
        }

        cell += char;
    }

    if (cell !== '' || row.length > 0) {
        row.push(cell);
        rows.push(row);
    }

    return rows;
};

const detectDelimiter = (csv: string): string => {
    const firstLine = csv.split(/\r?\n/, 1)[0] || '';
    const commaCount = (firstLine.match(/,/g) || []).length;
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    return semicolonCount > commaCount ? ';' : ',';
};

const createXlsxWorkbook = (rows: string[][], sheetName: string): Uint8Array => {
    const worksheetXml = createWorksheetXml(rows);
    const safeSheetName = escapeXml(sheetName.slice(0, 31) || 'Sheet1');

    return createZip([
        {
            path: '[Content_Types].xml',
            content:
                '<?xml version="1.0" encoding="UTF-8"?>' +
                '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
                '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
                '<Default Extension="xml" ContentType="application/xml"/>' +
                '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
                '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
                '</Types>',
        },
        {
            path: '_rels/.rels',
            content:
                '<?xml version="1.0" encoding="UTF-8"?>' +
                '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
                '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
                '</Relationships>',
        },
        {
            path: 'xl/workbook.xml',
            content:
                '<?xml version="1.0" encoding="UTF-8"?>' +
                '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
                `<sheets><sheet name="${safeSheetName}" sheetId="1" r:id="rId1"/></sheets>` +
                '</workbook>',
        },
        {
            path: 'xl/_rels/workbook.xml.rels',
            content:
                '<?xml version="1.0" encoding="UTF-8"?>' +
                '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
                '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
                '</Relationships>',
        },
        { path: 'xl/worksheets/sheet1.xml', content: worksheetXml },
    ]);
};

const createWorksheetXml = (rows: string[][]): string => {
    const rowXml = rows
        .map((row, rowIndex) => {
            const cells = row
                .map((value, columnIndex) => {
                    const reference = `${columnName(columnIndex + 1)}${rowIndex + 1}`;
                    return `<c r="${reference}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(
                        value,
                    )}</t></is></c>`;
                })
                .join('');
            return `<row r="${rowIndex + 1}">${cells}</row>`;
        })
        .join('');

    return (
        '<?xml version="1.0" encoding="UTF-8"?>' +
        '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
        `<sheetData>${rowXml}</sheetData>` +
        '</worksheet>'
    );
};

const columnName = (column: number): string => {
    let name = '';
    let current = column;

    while (current > 0) {
        const remainder = (current - 1) % 26;
        name = String.fromCharCode(65 + remainder) + name;
        current = Math.floor((current - 1) / 26);
    }

    return name;
};

const escapeXml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');

interface ZipFile {
    path: string;
    content: string;
}

const createZip = (files: ZipFile[]): Uint8Array => {
    const localParts: Uint8Array[] = [];
    const centralParts: Uint8Array[] = [];
    let offset = 0;

    files.forEach((file) => {
        const nameBytes = textEncoder.encode(file.path);
        const contentBytes = textEncoder.encode(file.content);
        const crc = crc32(contentBytes);

        const localHeader = createZipHeader(0x04034b50, nameBytes, contentBytes, crc);
        localParts.push(localHeader, contentBytes);
        centralParts.push(createCentralDirectoryHeader(nameBytes, contentBytes, crc, offset));
        offset += localHeader.length + contentBytes.length;
    });

    const centralDirectory = concatBytes(centralParts);
    const endRecord = createEndRecord(files.length, centralDirectory.length, offset);

    return concatBytes([...localParts, centralDirectory, endRecord]);
};

const createZipHeader = (
    signature: number,
    nameBytes: Uint8Array,
    contentBytes: Uint8Array,
    crc: number,
): Uint8Array => {
    const header = new Uint8Array(30 + nameBytes.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, signature, true);
    view.setUint16(4, 20, true);
    view.setUint16(6, 0, true);
    view.setUint16(8, 0, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, contentBytes.length, true);
    view.setUint32(22, contentBytes.length, true);
    view.setUint16(26, nameBytes.length, true);
    header.set(nameBytes, 30);
    return header;
};

const createCentralDirectoryHeader = (
    nameBytes: Uint8Array,
    contentBytes: Uint8Array,
    crc: number,
    offset: number,
): Uint8Array => {
    const header = new Uint8Array(46 + nameBytes.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, 0x02014b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(6, 20, true);
    view.setUint16(10, 0, true);
    view.setUint32(16, crc, true);
    view.setUint32(20, contentBytes.length, true);
    view.setUint32(24, contentBytes.length, true);
    view.setUint16(28, nameBytes.length, true);
    view.setUint32(42, offset, true);
    header.set(nameBytes, 46);
    return header;
};

const createEndRecord = (fileCount: number, directorySize: number, directoryOffset: number) => {
    const record = new Uint8Array(22);
    const view = new DataView(record.buffer);
    view.setUint32(0, 0x06054b50, true);
    view.setUint16(8, fileCount, true);
    view.setUint16(10, fileCount, true);
    view.setUint32(12, directorySize, true);
    view.setUint32(16, directoryOffset, true);
    return record;
};

const concatBytes = (parts: Uint8Array[]): Uint8Array => {
    const output = new Uint8Array(parts.reduce((total, part) => total + part.length, 0));
    let offset = 0;
    parts.forEach((part) => {
        output.set(part, offset);
        offset += part.length;
    });
    return output;
};

const crc32 = (bytes: Uint8Array): number => {
    let crc = 0xffffffff;
    for (const byte of bytes) {
        crc ^= byte;
        for (let bit = 0; bit < 8; bit += 1) {
            crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
        }
    }
    return (crc ^ 0xffffffff) >>> 0;
};
